/**
 * Avatar API Service
 * IDENTITY:P2.D — Avatar API Client
 *
 * All functions use fetchAPI wrapper for auth headers and CSRF tokens.
 */

import { fetchAPI } from './api.js';

const AVATAR_BASE = '/identity/avatar';

/**
 * Generate 4 avatar options.
 * @param {string|null} stylePreset - One of 6 style presets
 * @param {string|null} customPrompt - Custom creative direction (max 500 chars)
 * @param {string|null} paymentIntentId - Stripe PaymentIntent ID for paid generations
 * @returns {Promise<{options: Array, generation_id: string}>}
 */
export async function generateAvatar(stylePreset, customPrompt, paymentIntentId = null) {
  const body = {};
  if (stylePreset) body.style_preset = stylePreset;
  if (customPrompt) body.custom_prompt = customPrompt;
  if (paymentIntentId) body.payment_intent_id = paymentIntentId;

  return fetchAPI(`${AVATAR_BASE}/generate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Select an avatar as the current one.
 * @param {string} avatarId - UUID of the avatar to select
 * @returns {Promise<object>} AvatarResponse
 */
export async function selectAvatar(avatarId) {
  return fetchAPI(`${AVATAR_BASE}/select/${avatarId}`, {
    method: 'POST',
  });
}

/**
 * Get current avatar or letter-badge fallback.
 * @returns {Promise<{avatar: object|null, fallback: {letter: string, background_color: string}|null}>}
 */
export async function getCurrentAvatar() {
  return fetchAPI(AVATAR_BASE);
}

/**
 * Get paginated avatar history.
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Items per page (max 100)
 * @returns {Promise<{avatars: Array, total: number, page: number, pages: number}>}
 */
export async function getAvatarHistory(page = 1, limit = 20) {
  return fetchAPI(`${AVATAR_BASE}/history?page=${page}&limit=${limit}`);
}

/**
 * Remove current avatar (revert to letter-badge fallback).
 * @returns {Promise<void>}
 */
export async function removeAvatar() {
  return fetchAPI(AVATAR_BASE, { method: 'DELETE' });
}

/**
 * Create Stripe PaymentIntent for avatar generation ($2.99).
 * @returns {Promise<{client_secret: string, amount_cents: number}>}
 */
export async function createAvatarPaymentIntent() {
  return fetchAPI(`${AVATAR_BASE}/payment-intent`, {
    method: 'POST',
  });
}
